/**
 * MentionTextarea — drop-in replacement for <Textarea> with @mention typeahead.
 *
 * Usage:
 *   <MentionTextarea value={text} onChange={setText}
 *     onMentionsChange={setMentionedIds}
 *     placeholder="..." />
 *
 * Behaviour:
 *  - Type `@` to open a popover with teammate suggestions.
 *  - Continue typing to filter; ↑/↓ to navigate; Enter/Tab to select; Esc to close.
 *  - Inserts `@FullName ` and tracks the user_id of every mention.
 *  - Suggestions come from /api/users (cached for 5 min in module memory).
 */
import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { Textarea } from './ui/textarea';
import { AtSign } from 'lucide-react';
import api from '../lib/api';

let _userCache = null;
let _userCacheAt = 0;
const CACHE_TTL = 5 * 60 * 1000;

async function fetchUsers() {
  const now = Date.now();
  if (_userCache && now - _userCacheAt < CACHE_TTL) return _userCache;
  try {
    const r = await api.get('/users');
    _userCache = (r.data.users || r.data || []).map(u => ({
      id: u.id || u.email,
      name: u.full_name || u.email,
      email: u.email,
      role: u.role,
    }));
    _userCacheAt = now;
  } catch {
    _userCache = [];
  }
  return _userCache;
}

// Find the @-token immediately preceding the caret. Returns {start, query} or null.
function getMentionToken(text, caret) {
  if (caret === 0) return null;
  // Walk back from caret until whitespace, newline, or @. Stop at start.
  let i = caret - 1;
  while (i >= 0) {
    const ch = text[i];
    if (ch === '@') {
      // Must be at start of input or preceded by whitespace
      if (i === 0 || /\s/.test(text[i - 1])) {
        return { start: i, query: text.slice(i + 1, caret) };
      }
      return null;
    }
    if (/\s/.test(ch)) return null;
    i--;
  }
  return null;
}

function getRoleColor(role) {
  switch (role) {
    case 'admin': return 'bg-rose-50 text-rose-700 border-rose-200';
    case 'manager': return 'bg-blue-50 text-blue-700 border-blue-200';
    case 'restricted_user': return 'bg-violet-50 text-violet-700 border-violet-200';
    case 'trainer': return 'bg-emerald-50 text-emerald-700 border-emerald-200';
    default: return 'bg-slate-50 text-slate-600 border-slate-200';
  }
}

export default function MentionTextarea({
  value,
  onChange,
  onMentionsChange,
  onKeyDown,
  placeholder,
  className,
  rows = 3,
  disabled = false,
  'data-testid': dataTestId,
}) {
  const ref = useRef(null);
  const [users, setUsers] = useState([]);
  const [open, setOpen] = useState(false);
  const [token, setToken] = useState(null); // { start, query }
  const [hi, setHi] = useState(0);

  useEffect(() => { fetchUsers().then(setUsers); }, []);

  // Re-extract mentions whenever value changes
  useEffect(() => {
    if (!onMentionsChange) return;
    const re = /@([A-Za-z][A-Za-z0-9._\- ]{0,40})/g;
    const found = new Set();
    let m;
    while ((m = re.exec(value || '')) !== null) {
      const candidate = m[1].trim();
      // Match longest user name that is a prefix or equals candidate
      const u = users.find(u => candidate === u.name || candidate.startsWith(u.name + ' '));
      if (u) found.add(u.id);
    }
    onMentionsChange(Array.from(found));
  }, [value, users, onMentionsChange]);

  const filtered = useMemo(() => {
    if (!token) return [];
    const q = (token.query || '').toLowerCase();
    if (!q) return users.slice(0, 6);
    return users
      .filter(u => u.name.toLowerCase().includes(q) || (u.email || '').toLowerCase().includes(q))
      .slice(0, 6);
  }, [token, users]);

  // Reset highlight when filter changes
  useEffect(() => { setHi(0); }, [token?.query, open]);

  const handleChange = (e) => {
    const newVal = e.target.value;
    onChange(newVal);
    const caret = e.target.selectionStart || 0;
    const tk = getMentionToken(newVal, caret);
    setToken(tk);
    setOpen(!!tk);
  };

  const handleClick = (e) => {
    const caret = e.target.selectionStart || 0;
    const tk = getMentionToken(value || '', caret);
    setToken(tk);
    setOpen(!!tk);
  };

  const insertMention = useCallback((user) => {
    if (!ref.current || !token) return;
    const before = (value || '').slice(0, token.start);
    const caret = ref.current.selectionStart || 0;
    const after = (value || '').slice(caret);
    const inserted = `@${user.name} `;
    const next = before + inserted + after;
    onChange(next);
    setOpen(false);
    setToken(null);
    // Restore caret position after the inserted mention
    requestAnimationFrame(() => {
      if (!ref.current) return;
      const pos = before.length + inserted.length;
      ref.current.focus();
      ref.current.setSelectionRange(pos, pos);
    });
  }, [value, token, onChange]);

  const handleKey = (e) => {
    if (open && filtered.length > 0) {
      if (e.key === 'ArrowDown') { e.preventDefault(); setHi(h => (h + 1) % filtered.length); return; }
      if (e.key === 'ArrowUp') { e.preventDefault(); setHi(h => (h - 1 + filtered.length) % filtered.length); return; }
      if (e.key === 'Enter' || e.key === 'Tab') { e.preventDefault(); insertMention(filtered[hi]); return; }
      if (e.key === 'Escape') { e.preventDefault(); setOpen(false); return; }
    }
    // Forward unhandled keys to parent (e.g., Enter-to-submit when no @mention picker is open)
    if (onKeyDown) onKeyDown(e);
  };

  return (
    <div className="relative">
      <Textarea
        ref={ref}
        value={value || ''}
        onChange={handleChange}
        onClick={handleClick}
        onKeyDown={handleKey}
        onBlur={() => setTimeout(() => setOpen(false), 120)}
        placeholder={placeholder}
        rows={rows}
        disabled={disabled}
        className={className}
        data-testid={dataTestId}
      />

      {open && filtered.length > 0 && (
        <div
          className="absolute z-50 left-0 right-0 mt-1 rounded-md border border-slate-200 bg-white shadow-lg overflow-hidden"
          data-testid="mention-suggestions"
        >
          <div className="px-3 py-1.5 text-[9px] uppercase tracking-wider text-slate-400 font-semibold bg-slate-50 flex items-center gap-1">
            <AtSign className="w-2.5 h-2.5" /> Mention a teammate
          </div>
          <div className="max-h-52 overflow-y-auto">
            {filtered.map((u, idx) => (
              <button
                key={u.id}
                type="button"
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => insertMention(u)}
                onMouseEnter={() => setHi(idx)}
                className={`w-full text-left flex items-center gap-2 px-3 py-2 text-xs transition-colors ${
                  idx === hi ? 'bg-violet-50' : 'hover:bg-slate-50'
                }`}
                data-testid={`mention-suggestion-${u.id}`}
              >
                <div className="w-6 h-6 rounded-full bg-violet-100 text-violet-700 flex items-center justify-center text-[10px] font-bold flex-shrink-0">
                  {u.name.split(' ').map(n => n[0]).slice(0, 2).join('').toUpperCase()}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-slate-800 truncate">{u.name}</div>
                  <div className="text-[10px] text-slate-400 truncate">{u.email}</div>
                </div>
                {u.role && (
                  <span className={`text-[9px] px-1.5 py-0.5 rounded-full border ${getRoleColor(u.role)}`}>
                    {u.role.replace('_', ' ')}
                  </span>
                )}
              </button>
            ))}
          </div>
          <div className="px-3 py-1 text-[9px] text-slate-400 bg-slate-50/60 border-t border-slate-100 flex items-center justify-between">
            <span>↑↓ to navigate · ↵ to select · esc to close</span>
            <span className="italic">prototype</span>
          </div>
        </div>
      )}
    </div>
  );
}
